// @ts-nocheck

import React, { FC, useEffect, useMemo, useState, ChangeEvent } from 'react';

import dayjs from 'dayjs';

import DeleteIcon from '@mui/icons-material/Delete';
import { Button, Switch, Typography, Box, TextField, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridRowParams,
  GridCellParams,
  GridActionsCellItem,
  gridClasses,
} from '@mui/x-data-grid';

import { BaseModal } from '@shared/components/BaseModal';
import type { GameKey } from '@shared/types';
import { exportFile } from '@shared/utils';

import { generateGameLink } from '../../Constructors/ClientMemory/client.utils';
// import ClipboardBtn from '../../Constructors/ClientMemory/ClipboardBtn';
import { useExportGameKeys, useUpdateGameKeyField, useDeleteMultipleGameKeys } from '../master.actions';

import ConfirmationDialog from './ConfirmationDialog';
import FileManagerModal from './FileManagerModal';
import type { KeysTableProps } from './keys-table.types';

import styles from './index.module.scss';

const { tableHeaderContainer, exportLimitContainer } = styles;

const CopyLinkButton: FC<{ textToCopy: string; title?: string; isConstructor?: boolean }> = ({ textToCopy, title, isConstructor = false }) => {
  const [copied, setCopied] = useState(false);

  const fallbackCopyText = (text: string): boolean => {
    try {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.setAttribute('readonly', '');
      textArea.style.position = 'fixed';
      textArea.style.top = '-1000px';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      let successful = false;
      try {
        successful = document.execCommand('copy');
      } catch (_) {
        successful = false;
      }
      document.body.removeChild(textArea);
      return successful;
    } catch (_e) {
      return false;
    }
  };

  const handleCopy = async () => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(textToCopy);
        setCopied(true);
        setTimeout(() => setCopied(false), 1800);
        return;
      }
      // Фолбэк для мобильных/небезопасных контекстов
      const ok = fallbackCopyText(textToCopy);
      if (ok) {
        setCopied(true);
        setTimeout(() => setCopied(false), 1800);
        return;
      }
      // Последний шанс — показать prompt для ручного копирования
      window.prompt((window as any).i18n?.t ? (window as any).i18n.t('clientAll.copyPrompt') : 'Скопируйте ссылку:', textToCopy);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (e) {
      const ok = fallbackCopyText(textToCopy);
      if (!ok) {
        window.prompt((window as any).i18n?.t ? (window as any).i18n.t('clientAll.copyPrompt') : 'Скопируйте ссылку:', textToCopy);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    }
  };

  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <button
        onClick={handleCopy}
        className={`partner-dashboard__copy-icon-btn partner-dashboard__copy-icon-btn--large${copied ? ' is-copied' : ''}`}
        title={title || 'Copy link'}
        style={isConstructor ? {
          minWidth: '120px',
          backgroundColor: '#ff9800',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          padding: '6px 12px',
          fontSize: '12px',
          cursor: 'pointer',
          fontWeight: 'bold',
          display: 'flex',
          alignItems: 'center',
          gap: '4px'
        } : {}}
      >
        {isConstructor ? (
          <>
            {copied ? '✅' : '🏗️'} 
            {copied ? (window as any).i18n?.t ? (window as any).i18n.t('clientAll.copied') : 'Done' : 'Конструктор'}
          </>
        ) : (
          <>
            {copied ? '✅' : '📋'} 
            {copied ? (window as any).i18n?.t ? (window as any).i18n.t('clientAll.copied') : 'Done' : ''}
          </>
        )}
      </button>
    </div>
  );
};

const KeysTable: FC<KeysTableProps> = ({
  gameKeys = [],
  onDeleteClick,
  onBulkDeleteClick,
  refetch,
}) => {
  const [localGameKeys, setLocalGameKeys] = useState([]);
  const [filteredGameKeys, setFilteredGameKeys] = useState([]);
  const [open, setOpen] = React.useState(false);
  const [selectedKey, setSelectedKey] = useState<string>('');
  const [exportLimit, setExportLimit] = useState<number>(0);
  const [isExportModalOpened, setIsExportModalOpened] = useState(false);
  const [isFileManagerOpen, setIsFileManagerOpen] = useState(false);
  const [fileManagerKeyId, setFileManagerKeyId] = useState<string>('');
  const [fileManagerKey, setFileManagerKey] = useState<string>('');
  const [partnerFilter, setPartnerFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [isBulkDeleteModalOpen, setIsBulkDeleteModalOpen] = useState(false);



  const [_, updateGameKeyField] = useUpdateGameKeyField();
  const [exportResponse, exportGameKeys] = useExportGameKeys();
  const [__, deleteMultipleGameKeys, bulkDeleteError] = useDeleteMultipleGameKeys();

  const getEditorUrl = (key: string) => `${window.location.origin}/login?key=${key}`;
  const getPlayUrl = (buildId: string) => `${window.location.origin}/game?buildId=${buildId}`;
  const handleEditorClick = (key: string) => window.open(getEditorUrl(key), '_blank');
  const handleGameClick = (buildId: string) => window.open(getPlayUrl(buildId), '_blank');
  const copyToClipboard = (text: string) => navigator.clipboard.writeText(text);

  // Получаем уникальные партнеры для фильтра
  const uniquePartners = React.useMemo(() => {
    const partners = new Set<string>();
    localGameKeys.forEach(key => {
      if (key.partnerEmail) {
        partners.add(key.partnerEmail);
      }
    });
    return Array.from(partners).sort();
  }, [localGameKeys]);

  useEffect(() => {
    // console.log('🔄 KeysTable: gameKeys updated:', gameKeys?.length || 0, 'keys');
    setLocalGameKeys(gameKeys || []);
  }, [gameKeys]);

  // Фильтрация по партнеру, поиск и сортировка по алфавиту
  useEffect(() => {
    // console.log('🔄 KeysTable: localGameKeys updated:', localGameKeys?.length || 0, 'keys');
    
    let next = localGameKeys;

    if (partnerFilter === 'my') {
      next = next.filter((key) => !key.partnerEmail || key.partnerEmail === '');
    } else if (partnerFilter !== 'all') {
      next = next.filter((key) => key.partnerEmail === partnerFilter);
    }

    const normalizedQuery = searchQuery.trim().toLowerCase();
    if (normalizedQuery) {
      next = next.filter((key) => {
        const fields = [
          key.gameName,
          key.notion,
          key.key,
          key.email,
          key.firstName,
          key.partnerEmail,
        ]
          .filter(Boolean)
          .map((v) => String(v).toLowerCase());
        return fields.some((v) => v.includes(normalizedQuery));
      });
    }

    next = [...next].sort((a, b) => {
      const dateA = new Date(a.createdAt || 0);
      const dateB = new Date(b.createdAt || 0);
      return dateB.getTime() - dateA.getTime(); // Сортировка по убыванию (новые сначала)
    });

    // console.log('🔄 KeysTable: filteredGameKeys updated:', next?.length || 0, 'keys');
    setFilteredGameKeys(next);
  }, [localGameKeys, partnerFilter, searchQuery]);

  useEffect(() => {
    if (exportResponse) {
      const exportGameKeysResponse = async () => {
        await exportFile(exportResponse, 'game_keys');
      };

      exportGameKeysResponse();
    }
  }, [exportResponse]);

  const handleDeleteKeyClick = (id: string) => {
    setSelectedKey(id);

    setOpen(true);
  };

  const handleUpdateGameKeyActiveStatus = async (
    id: string,
    isActive: boolean
  ) => {
    await updateGameKeyField(id, { isActive });

    setLocalGameKeys((previousGameKeys) => {
      const updatedGameKeys = previousGameKeys.map((gameKey) => {
        const { _id: currentGameKeyId } = gameKey;

        if (currentGameKeyId === id) {
          return { ...gameKey, isActive };
        }

        return gameKey;
      });

      return updatedGameKeys;
    });
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleDeleteKeyConfirmationClick = () => {
    void onDeleteClick(selectedKey).then(() => refetch());

    setOpen(false);
  };

  const handleBulkDeleteClick = () => {
    setIsBulkDeleteModalOpen(true);
  };

  const handleBulkDeleteConfirmation = async () => {
    try {
      console.log('Starting bulk delete with IDs:', selectedRows);
      if (onBulkDeleteClick) {
        await onBulkDeleteClick(selectedRows);
      } else {
        await deleteMultipleGameKeys(selectedRows);
      }
      console.log('Bulk delete completed successfully');
      setSelectedRows([]);
      setIsBulkDeleteModalOpen(false);
      console.log('Calling refetch to update the list...');
      refetch();
      console.log('Refetch called successfully');
    } catch (error) {
      console.error('Ошибка при массовом удалении:', error);
    }
  };

  const handleBulkDeleteCancel = () => {
    setIsBulkDeleteModalOpen(false);
  };

  const handleOpenExportModal = () => {
    setIsExportModalOpened(true);
  };

  const handleCloseExportModal = () => {
    setIsExportModalOpened(false);

    setExportLimit(0);
  };

  const onExportSubmit = async () => {
    await exportGameKeys(exportLimit);

    handleCloseExportModal();
  };

  const onLimitChange = (event: ChangeEvent<HTMLInputElement>) => {
    setExportLimit(event.target.value);
  };

  const handlePartnerFilterChange = (event: any) => {
    setPartnerFilter(event.target.value);
  };

  const rows = filteredGameKeys.map(
    ({
      gameName,
      key,
      _id,
      endEditDate,
      endGameDate,
      activateDate,
      isActive,
      runCount,
      gameBuildId,
      gameBuild,
      notion,
      firstName,
      email,
      isWhiteLabel,
      partnerEmail,
      createdAt,
      s3FolderUrl,
    }) => ({
      gameName,
      key,
      id: _id,
      endEditDate,
      endGameDate,
      activateDate,
      gameBuildId,
      gameBuild,
      isActive,
      runCount,
      notion,
      _id,
      firstName,
      email,
      isWhiteLabel,
      partnerEmail,
      createdAt,
      s3FolderUrl,
    })
  );

  const columns: GridColDef[] = [
    {
      field: 'gameName',
      headerName: 'Game name',
      width: 120,
      editable: false,
    },
    {
      field: 'links',
      headerName: 'Ссылки',
      minWidth: 160,
      flex: 1,
      sortable: false,
      editable: false,
      renderCell: (params: GridCellParams<GameKey>) => {
        const {
          row: { key, gameBuildId, gameBuild },
        } = params as any;

        return (
          <div className="partner-dashboard__links">
            <div className="partner-dashboard__link-group">
              <CopyLinkButton
                textToCopy={getEditorUrl(key)}
                title="Копировать ссылку на редактор"
                isConstructor={true}
              />
            </div>
            {gameBuild && (
              <div className="partner-dashboard__link-group">
                <button
                  onClick={() => handleGameClick(gameBuildId)}
                  className="partner-dashboard__link-btn"
                >
                  Game
                </button>
                <CopyLinkButton
                  textToCopy={getPlayUrl(gameBuildId)}
                  title="Копировать ссылку на игру"
                />
              </div>
            )}
          </div>
        );
      },
    },
    {
      field: 'runCount',
      headerName: 'Run Count',
      width: 60,
      editable: false,
    },
    {
      field: 'key',
      headerName: 'Key',
      minWidth: 300,
      flex: 1,
      editable: false,
    },
    {
      field: 'activateDate',
      headerName: 'Activate Date',
      minWidth: 90,
      flex: 1,
      editable: false,
      renderCell: (params: GridCellParams<Date | null>) => {
        const value =
          params.value === null
            ? 'Not activated'
            : dayjs(params.value).format('DD/MM/YYYY HH:mm:ss');
        const color = params.value === null ? 'red' : 'green';

        return (
          <Typography fontSize="12px" color={color}>
            {value}
          </Typography>
        );
      },
    },
    {
      field: 'notion',
      headerName: 'Notion',
      flex: 1,
      minWidth: 80,
      editable: false,
    },
    {
      field: 'firstName',
      headerName: 'Name',
      flex: 1,
      minWidth: 70,
      editable: false,
    },
    {
      field: 'email',
      headerName: 'Email',
      flex: 1,
      minWidth: 80,
      editable: false,
    },
    
    {
      field: 'partnerEmail',
      headerName: 'Партнер',
      flex: 1,
      minWidth: 150,
      editable: false,
      renderCell: (params: GridCellParams<string | null>) => {
        const value = params.value || 'Нет партнера';
        return (
          <Typography fontSize="14px" color={params.value ? 'primary' : 'text.secondary'}>
            {value}
          </Typography>
        );
      },
    },
    {
      field: 'amazon',
      headerName: 'Amazon',
      flex: 1,
      minWidth: 210,
      editable: false,
      renderCell: (params: GridCellParams<GameKey>) => {
        const {
          row: { key, s3FolderUrl, id },
        } = params as any;

        // Ссылка на AWS S3 консоль для папки игры
        const getS3ConsoleUrl = (gameKey: string) => {
          const bucketName = 'eatsy-games-assets-dev';
          const region = 'eu-central-1';
          const prefix = `${gameKey}/`;
          return `https://${region}.console.aws.amazon.com/s3/buckets/${bucketName}?region=${region}&bucketType=general&prefix=${encodeURIComponent(prefix)}&showversions=false&tab=objects`;
        };

        const folderUrl = s3FolderUrl || getS3ConsoleUrl(key);

        const openFileManager = () => {
          setFileManagerKeyId(id);
          setFileManagerKey(key);
          setIsFileManagerOpen(true);
        };

        return (
          <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
            <button
              onClick={() => window.open(folderUrl, '_blank')}
              style={{
                padding: '4px 8px',
                fontSize: '12px',
                backgroundColor: '#1976d2',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                minWidth: '60px'
              }}
              title="Открыть папку в AWS S3 консоли"
            >
              S3
            </button>
            <button
              onClick={openFileManager}
              style={{
                padding: '4px 8px',
                fontSize: '12px',
                backgroundColor: '#455a64',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                minWidth: '60px'
              }}
              title="Открыть файловый менеджер"
            >
              📁
            </button>
          </div>
        );
      },
    },
    {
      field: 'endEditDate',
      headerName: 'End Edit Date',
      minWidth: 150,
      flex: 1,
      editable: false,
      renderCell: (params: GridCellParams<Date | null>) => {
        const value =
          params.value === null
            ? 'Not existing'
            : dayjs(params.value).format('DD/MM/YYYY HH:mm:ss');
        const color = params.value === null ? 'red' : 'green';

        return (
          <Typography fontSize="14px" color={color}>
            {value}
          </Typography>
        );
      },
    },
    {
      field: 'endGameDate',
      headerName: 'End Game Date',
      minWidth: 150,
      flex: 1,
      editable: false,
      renderCell: (params: GridCellParams<Date | null>) => {
        const value =
          params.value === null
            ? 'Not existing'
            : dayjs(params.value).format('DD/MM/YYYY HH:mm:ss');
        const color = params.value === null ? 'red' : 'green';

        return (
          <Typography fontSize="14px" color={color}>
            {value}
          </Typography>
        );
      },
    },
    {
      field: 'isWhiteLabel',
      headerName: 'White label',
      width: 70,
      editable: false,
    },
    {
      field: 'isActive',
      headerName: 'Active',
      minWidth: 50,
      editable: false,
      renderCell: (params: GridCellParams<GameKey>) => {
        const {
          row: { isActive, id },
        } = params;

        return (
          <Switch
            checked={isActive}
            onChange={(event) =>
              handleUpdateGameKeyActiveStatus(id, event.target.checked)
            }
          />
        );
      },
    },
    {
      field: 'createdAt',
      headerName: 'Дата создания',
      minWidth: 150,
      flex: 1,
      editable: false,
      renderCell: (params: GridCellParams<Date>) => {
        const value = dayjs(params.value).format('DD/MM/YYYY HH:mm');
        return (
          <Typography fontSize="12px">
            {value}
          </Typography>
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      type: 'actions',
      minWidth: 50,
      getActions: (params: GridRowParams) => [
        <GridActionsCellItem
          icon={<DeleteIcon />}
          label="Delete key"
          onClick={() => handleDeleteKeyClick(params.row.id)}
        />,
      ],
    },
  ];

  return (
    <>
      <Box>
        <Box className={tableHeaderContainer}>
          <Typography marginBottom="20px" variant="h5" noWrap component="div">
            Ключи
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', marginBottom: 2 }}>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Фильтр по партнеру</InputLabel>
              <Select
                value={partnerFilter}
                label="Фильтр по партнеру"
                onChange={handlePartnerFilterChange}
              >
                <MenuItem value="all">Все ключи</MenuItem>
                <MenuItem value="my">Мои ключи</MenuItem>
                {uniquePartners.map((partner) => (
                  <MenuItem key={partner} value={partner}>
                    {partner}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small"
              placeholder="Поиск по игре, ключу, заметке, email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ minWidth: 320 }}
            />
            <Button size="small" variant="contained" onClick={handleOpenExportModal}>
              Экспорт Excel
            </Button>
            {selectedRows.length > 0 && (
              <Button 
                size="small" 
                variant="contained" 
                color="error"
                onClick={handleBulkDeleteClick}
                sx={{ 
                  fontWeight: 'bold',
                  minWidth: '200px',
                  backgroundColor: '#d32f2f',
                  '&:hover': {
                    backgroundColor: '#b71c1c'
                  }
                }}
              >
                🗑️ Удалить выбранные ({selectedRows.length})
              </Button>
            )}
          </Box>
        </Box>
      </Box>

      <DataGrid
        autoHeight={false}
        getRowId={(row) => row.id}
        rowCount={1}
        getRowHeight={() => 'auto'}
        rows={rows}
        sx={{
          height: '80vh',
          width: '100%',
          [`& .${gridClasses.cell}`]: {
            py: 1,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-all',
          },
        }}
        columns={columns}
        scrollX
        checkboxSelection
        disableRowSelectionOnClick
        onSelectionModelChange={(newSelectionModel) => {
          setSelectedRows(newSelectionModel as string[]);
        }}
        selectionModel={selectedRows}
        initialState={{
          pagination: {
            paginationModel: { page: 0, pageSize: 25 },
          },
        }}
      />
      <ConfirmationDialog
        isOpen={open}
        onConfirm={handleDeleteKeyConfirmationClick}
        onClose={handleClose}
      />
      <BaseModal
        headerTitle="Export Exel"
        isOpened={isExportModalOpened}
        onSubmit={onExportSubmit}
        handleClose={handleCloseExportModal}
        buttonConfirmedText="Export"
      >
        <Box className={exportLimitContainer}>
          <TextField
            label="Enter limit"
            value={exportLimit}
            size="small"
            type="number"
            onChange={onLimitChange}
          />
        </Box>
      </BaseModal>
      <BaseModal
        headerTitle="Массовое удаление ключей"
        isOpened={isBulkDeleteModalOpen}
        onSubmit={handleBulkDeleteConfirmation}
        handleClose={handleBulkDeleteCancel}
        buttonConfirmedText="Удалить"
        buttonCancelText="Отмена"
      >
        <Box>
          <Typography>
            Вы уверены, что хотите удалить {selectedRows.length} выбранных ключей?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Это действие нельзя отменить.
          </Typography>
        </Box>
      </BaseModal>
      <FileManagerModal
        open={isFileManagerOpen}
        gameKeyId={fileManagerKeyId}
        gameKey={fileManagerKey}
        onClose={() => setIsFileManagerOpen(false)}
      />
    </>
  );
};

export default KeysTable;
